import {
  InputGroup,
  InputRightAddon,
  HStack,
  Box,
  Input,
  FormControl,
  useColorMode,
  useToast,
} from '@chakra-ui/react';
import {
  profitTextColor,
  profitBgColor,
  evaluateProfit,
  roundedPurchasePriceWithFees,
  setValueAsDecimalExtender,
  validateNotNaN,
} from '../shared';
import { useEffect } from 'react';
import {
  AutoUpdateResponseCallback,
  Position,
} from '@texas/components/shared/autoUpdate/types';
import {
  useAutoUpdateForm,
  AutoUpdateWrapper,
} from '@texas/components/shared/autoUpdate/useAutoUpdateForm';
import {
  exchangeRateCalculation,
  round,
} from '@texas/utils/helpers/numberHelpers';
import { PurchaseInformation } from '@texas/api/endpoints/purchaseInformationApi';
import { SalesCurrency } from '@texas/api/endpoints/salesCurrenciesApi';
import {
  SalesCellPrice,
  SalesPrice,
  AutoUpdateSalesCellPrice,
  AutoUpdateSalesCellPriceAdditionalData,
  tierPriceApi,
  EMPTY_AUTO_UPDATE_SALES_PRICE_OBJECT,
} from '@texas/api/endpoints/tierPriceApi';
import { Margin } from '../common/Margin';
import { Multiplied } from '../common/Multiplied';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { useTranslation } from 'react-i18next';

interface SalesCellPriceFormProps {
  salesCell?: SalesCellPrice;
  salesPrice?: SalesPrice;
  purchasePrice: number;
  decimalsInPrice: number;
  tierPriceId: number;
  cellX: number;
  cellY: number;
  salesCurrency: SalesCurrency;
  purchaseInformation: PurchaseInformation;
  sellingExchangeRate: number;
  showCurrencyCode?: boolean;
  isDisabled?: boolean;
  onUpdate?: (cell: SalesCellPrice) => void;
}

export function SalesCellPriceForm({
  showCurrencyCode = true,
  purchasePrice,
  decimalsInPrice,
  tierPriceId,
  cellX,
  cellY,
  salesCell,
  salesCurrency,
  purchaseInformation,
  sellingExchangeRate,
  salesPrice,
  onUpdate,
  isDisabled = false,
}: SalesCellPriceFormProps) {
  const { colorMode } = useColorMode();
  const { t } = useTranslation();
  const toast = useToast();

  const {
    registerAutoUpdate,
    watch,
    getValues,
    resetValue,
    setRequestParams,
    form,
    formState: { errors },
    register,
    triggerSubmit,
    setValue,
  } = useAutoUpdateForm<
    AutoUpdateSalesCellPrice,
    SalesCellPrice,
    AutoUpdateSalesCellPriceAdditionalData
  >({
    defaultValue: {
      price: salesCell?.price ?? salesPrice?.price ?? 0,
    },
    requestParams: [salesCell?.id ?? 0],
    namePrefix: 'salesCellPrice',
    autoUpdateRequest: tierPriceApi.autoUpdateSalesCellPrice,
    fallbackMapObject: EMPTY_AUTO_UPDATE_SALES_PRICE_OBJECT,
    additionalData: {
      tierPriceId: tierPriceId,
      salesCurrencyId: salesCurrency.id,
      matrixNodeValueXId: cellX,
      matrixNodeValueYId: cellY,
    },
    onResponse,
  });

  function onResponse(
    data: AutoUpdateResponseCallback<SalesCellPrice, AutoUpdateSalesCellPrice>,
  ) {
    setRequestParams([data.unmodifiedServerData.id]);

    if (onUpdate)
      onUpdate({ ...data.unmodifiedServerData, ...data.mappedServerData });
  }

  useEffect(() => {
    const salesPriceValue = salesCell?.price ?? salesPrice?.price;
    if (salesPriceValue !== getValues('price')) {
      resetValue('price', salesCell?.price ?? salesPrice?.price ?? 0);
    }
    if (salesCell) {
      setRequestParams([salesCell.id]);
    }
  }, [salesCell, getValues, resetValue, setRequestParams, salesPrice?.price]);

  const watchPrice = watch('price');
  const profit = evaluateProfit(
    exchangeRateCalculation(
      watchPrice ?? 0,
      salesCurrency.fixedExchangeRate,
      sellingExchangeRate,
      decimalsInPrice,
    ),
    roundedPurchasePriceWithFees(
      purchasePrice,
      sellingExchangeRate,
      decimalsInPrice,
      purchaseInformation,
    ),
  );
  const color = profitTextColor(profit, colorMode === 'dark');
  const bgColor = profitBgColor(profit, colorMode === 'dark');

  return (
    <form style={{ width: 'fit-content' }} {...form}>
      <FormControl isDisabled={isDisabled}>
        <AutoUpdateWrapper
          autoUpdateProps={registerAutoUpdate}
          path="price"
          position={Position.Right}
        >
          <InputGroup
            padding={1}
            pr={0}
            bg={bgColor}
            minW={52}
            borderRadius={4}
            role="group"
          >
            <Input
              {...register('price', {
                required: true,
                min: { value: 0, message: t('errors.notNegative') },
                validate: (value) => validateNotNaN(value, t),
                setValueAs: (value) => setValueAsDecimalExtender(value, false),
                onBlur: () => {
                  const value = getValues('price') ?? 0;
                  const rounded = round(value, decimalsInPrice);
                  if (rounded !== value) {
                    toast({
                      title: t('general.valueRounded'),
                      status: 'info',
                      duration: 3000,
                      isClosable: true,
                    });
                  }
                  setValue('price', rounded);
                  triggerSubmit();
                },
              })}
              color={color}
              role="group"
              _placeholder={{
                color: color,
              }}
              placeholder="0"
            />

            <InputRightAddon px={2} pointerEvents="none">
              {showCurrencyCode && (
                <HStack
                  overflow="hidden"
                  transition="opacity 200ms ease, max-width 100ms ease"
                  _groupHover={{ opacity: 0, maxW: '0' }}
                  maxW="32px"
                  opacity={1}
                >
                  <Box>{salesCurrency.currencyCode}</Box>
                </HStack>
              )}

              <HStack
                overflow="hidden"
                transition="max-width 200ms ease"
                spacing={1}
                _groupHover={{ maxW: '200px' }}
                maxW="0px"
              >
                <Multiplied
                  salesCurrency={salesCurrency}
                  sellingExchangeRate={sellingExchangeRate}
                  price={watchPrice ?? 0}
                  purchasePrice={purchasePrice}
                  purchaseInformation={purchaseInformation}
                  decimalsInPrice={decimalsInPrice}
                />
                <Margin
                  salesCurrency={salesCurrency}
                  sellingExchangeRate={sellingExchangeRate}
                  price={watchPrice ?? 0}
                  purchasePrice={purchasePrice}
                  purchaseInformation={purchaseInformation}
                  decimalsInPrice={decimalsInPrice}
                />
              </HStack>
            </InputRightAddon>
          </InputGroup>
          <ErrorLabel text={errors.price?.message} />
        </AutoUpdateWrapper>
      </FormControl>
    </form>
  );
}
